/**
 * When somebody opens our email, and when Postmark stops sending to an address
 * — the words both sides say it in (Aaron, Oct 26: "does this mean we can
 * show when someone opens our emails in the app??? because that's amazing").
 * `docs/adapters/postmark.md` (Opens; Addresses Postmark stops sending to).
 *
 * ## What an open is, honestly
 *
 * Postmark tracks an open with a one-pixel picture in the email's HTML: when
 * the picture loads, it posts an Open. So *opened* means **the pictures
 * loaded**. Apple Mail's privacy protection loads every picture of every email
 * by itself, so an Apple Mail open can be the phone and not the person; and a
 * mail app that blocks pictures opens without telling anybody. Every place an
 * open is shown carries `OPENED_HONESTY` beside it.
 *
 * ## What is kept of the reader
 *
 * Names only: the mail app (Postmark's `Client.Name`, version numbers taken
 * off) and the kind of device (from its `OS` and `Platform`) — *Gmail on an
 * iPhone*. Postmark also sends the reader's IP address, the place it resolves
 * to and the full user agent; none of those is read here, so none of them can
 * be kept anywhere.
 */
/** The kinds of device an open can say it was on, each with its two ways of being said. */
export const OPEN_DEVICES = {
    iphone: { short: 'iPhone', long: 'an iPhone' },
    ipad: { short: 'iPad', long: 'an iPad' },
    android_phone: { short: 'Android', long: 'an Android phone' },
    android_tablet: { short: 'Android tablet', long: 'an Android tablet' },
    mac: { short: 'Mac', long: 'a Mac' },
    windows: { short: 'Windows', long: 'a Windows computer' },
    chromebook: { short: 'Chromebook', long: 'a Chromebook' },
    linux: { short: 'Linux', long: 'a Linux computer' },
    phone: { short: 'phone', long: 'a phone' },
    tablet: { short: 'tablet', long: 'a tablet' },
    computer: { short: 'computer', long: 'a computer' },
};
export const isOpenDevice = (value) => Boolean(value) && Object.prototype.hasOwnProperty.call(OPEN_DEVICES, value);
/**
 * A name without its version: *Chrome 35.0.1916.153* → *Chrome*, *iOS 16.1* →
 * *iOS*, *Outlook 2016* stays (a year is part of that name). *Unknown* is
 * nothing, and a name is never longer than a name.
 */
export function nameOnly(value) {
    const cleaned = (value ?? '')
        .replace(/[\u0000-\u001f\u007f]/g, ' ')
        .replace(/\s+v?\d+(\.\d+)+\S*/gi, '')
        .replace(/\s+/g, ' ')
        .trim();
    if (!cleaned || /^unknown$/i.test(cleaned))
        return null;
    return cleaned.slice(0, 60);
}
/** The kind of device, from the OS's name and family and Postmark's `Platform`. */
export function deviceOf(facts) {
    const os = `${facts.osFamily ?? ''} ${facts.os ?? ''}`.toLowerCase();
    const platform = (facts.platform ?? '').toLowerCase();
    const tablet = platform === 'tablet';
    if (/ipad/.test(os))
        return 'ipad';
    if (/iphone|\bios\b/.test(os))
        return tablet ? 'ipad' : 'iphone';
    if (/android/.test(os))
        return tablet ? 'android_tablet' : 'android_phone';
    if (/mac|os x/.test(os))
        return 'mac';
    if (/windows/.test(os))
        return 'windows';
    if (/chrome ?os/.test(os))
        return 'chromebook';
    if (/linux|ubuntu|fedora/.test(os))
        return 'linux';
    if (platform === 'mobile')
        return 'phone';
    if (tablet)
        return 'tablet';
    if (platform === 'desktop')
        return 'computer';
    return null;
}
/** What an open keeps of its reader: the app and the kind of device, as names. */
export function readerOf(facts) {
    return { client: nameOnly(facts.client), device: deviceOf(facts) };
}
/** *Gmail · iPhone* — the email log's column. Null when Postmark could say neither. */
export function readerShort(client, device) {
    const parts = [client?.trim() || null, isOpenDevice(device) ? OPEN_DEVICES[device].short : null]
        .filter((part) => Boolean(part));
    return parts.length > 0 ? parts.join(' · ') : null;
}
/** *in Gmail on an iPhone*, *in Gmail*, *on an iPhone*, or nothing. */
export function readerWhere(client, device) {
    return [
        client?.trim() ? `in ${client.trim()}` : null,
        isOpenDevice(device) ? `on ${OPEN_DEVICES[device].long}` : null,
    ].filter(Boolean).join(' ');
}
/** The one sentence of honesty that goes wherever an open is shown. */
export const OPENED_HONESTY = 'Opened means the email’s pictures loaded. Apple Mail can open emails by itself and '
    + 'some mail apps block pictures, so it’s a good sign, not proof.';
/** *· 3 times* when more than once; nothing for once. */
export const openedTimes = (count) => (count > 1 ? ` · ${count} times` : '');
/**
 * The hover line: where the latest open was, when the first was if it was
 * another, and the honesty. `when` says an instant in the viewer's words.
 */
export function openedTitle(opened, when) {
    const where = readerWhere(opened.client, opened.device);
    const lead = opened.count > 1
        ? `Last opened${where ? ` ${where}` : ''}; first opened ${when(opened.first)}.`
        : `Opened${where ? ` ${where}` : ''}.`;
    return `${lead} ${OPENED_HONESTY}`;
}
/** Postmark's `SuppressionReason` and `Origin`, as one of ours. */
export function stoppedReasonOf(reason, origin) {
    if (reason === 'HardBounce')
        return 'bounced';
    if (reason === 'SpamComplaint')
        return 'spam';
    if (reason === 'ManualSuppression' && origin === 'Recipient')
        return 'unsubscribed';
    return 'turned_off';
}
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dayLabel = (day) => {
    const [, month, date] = day.split('-').map(Number);
    return `${MONTHS[month - 1] ?? ''} ${date}`;
};
/**
 * *Postmark has stopped sending to this address — it bounced on Oct 26. Check
 * it with the customer.* The composer's warning, which never stops a send:
 * Postmark refuses it itself, and this is so nobody is surprised.
 */
export function stoppedWords(stopped, options) {
    const who = options.isTo ? 'this address' : stopped.address;
    const on = stopped.day ? ` on ${dayLabel(stopped.day)}` : '';
    const why = stopped.reason === 'bounced' ? `it bounced${on}`
        : stopped.reason === 'spam' ? `it reported one of our emails as spam${on}`
            : stopped.reason === 'unsubscribed' ? `they unsubscribed${on}`
                : stopped.reason === 'turned_off' ? `it was switched off at Postmark${on}`
                    : null;
    const advice = options.advice === false ? '' : ' Check it with the customer.';
    return `Postmark has stopped sending to ${who}${why ? ` — ${why}` : ''}.${advice}`;
}
//# sourceMappingURL=email-opens.js.map